/**
 * Kalite / Eğitim atamalarının son tamamlama tarihi takibi.
 *
 * Atamalar kişiye tek tek değil, çalışan grubuna (rol) göre yapılır:
 *   - SOPs.group          → ['Saha Koordinatörü', 'Hepsi', ...]
 *   - IndicationEducations.group
 *
 * Tamamlanma bilgisi ayrı koleksiyonlarda tutulur:
 *   - SOP     → userSOPReadConfirmations (userId = Firebase Auth UID, isRead)
 *   - Eğitim  → userEducationProgress    (userId = Firebase Auth UID, watchedProgress >= 50)
 */

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

/** Son tamamlama tarihine bu kadar gün kala uyarı devreye girer. */
export const DUE_SOON_DAYS = 2;

/** Eğitimin "tamamlandı" sayılması için gereken izleme yüzdesi. */
export const EDUCATION_COMPLETION_PERCENT = 50;

/** Bir SOP/eğitim dokümanının kaydedilmiş son tamamlama tarihi alanı. */
export const DUE_DATE_FIELD = 'dueDate';

/** 'YYYY-MM-DD' / Date / Firestore Timestamp → günün başına normalize Date. */
export const parseDueDate = (value) => {
  if (!value) return null;
  let date;
  if (typeof value.toDate === 'function') date = value.toDate();
  else if (value.seconds) date = new Date(value.seconds * 1000);
  else date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(23, 59, 59, 999); // son tarih günün sonuna kadar geçerli
  return date;
};

/** Son tarihe kalan tam gün sayısı. Negatif değer gecikmeyi ifade eder. */
export const daysUntil = (dueDate, now = new Date()) => {
  const due = parseDueDate(dueDate);
  if (!due) return null;
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfDue = new Date(due);
  startOfDue.setHours(0, 0, 0, 0);
  return Math.round((startOfDue - startOfToday) / 86400000);
};

/** Son 2 güne girdi mi (bugün ve gecikmiş olanlar dahil)? */
export const isDueSoon = (dueDate, now = new Date()) => {
  const days = daysUntil(dueDate, now);
  return days !== null && days <= DUE_SOON_DAYS;
};

export const isOverdue = (dueDate, now = new Date()) => {
  const days = daysUntil(dueDate, now);
  return days !== null && days < 0;
};

/** "2 gün kaldı" / "bugün son gün" / "3 gün gecikti" */
export const describeDeadline = (dueDate, now = new Date()) => {
  const days = daysUntil(dueDate, now);
  if (days === null) return 'Son tarih belirtilmemiş';
  if (days < 0) return `${Math.abs(days)} gün gecikti`;
  if (days === 0) return 'Bugün son gün';
  if (days === 1) return 'Son 1 gün';
  return `${days} gün kaldı`;
};

/**
 * Bir role atanmış SOP ve eğitimleri getirir.
 * 'Hepsi' grubu herkese atanmış sayılır.
 */
export const fetchAssignmentsForRole = async (role) => {
  const groups = ['Hepsi'];
  if (role) groups.push(role);

  const readGroup = async (collectionName, group) => {
    const snap = await getDocs(
      query(collection(db, collectionName), where('group', 'array-contains', group))
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  const [sopResults, educationResults] = await Promise.all([
    Promise.all(groups.map((g) => readGroup('SOPs', g))),
    Promise.all(groups.map((g) => readGroup('IndicationEducations', g))),
  ]);

  // Aynı doküman birden fazla grup sorgusundan gelebilir
  const dedupe = (lists, type) => {
    const map = new Map();
    lists.flat().forEach((item) => map.set(item.id, { ...item, assignmentType: type }));
    return Array.from(map.values());
  };

  return [
    ...dedupe(sopResults, 'SOP'),
    // Eğitimlerde yalnızca video içerenler gerçek atama sayılır (ProfilePage ile aynı kural)
    ...dedupe(educationResults, 'Eğitim').filter((e) => e.videoUrl),
  ];
};

/** Kullanıcının tamamladığı SOP ve eğitim id'leri. */
export const fetchCompletedIds = async (authUid) => {
  if (!authUid) return { sops: new Set(), educations: new Set() };

  const [sopSnap, eduSnap] = await Promise.all([
    getDocs(query(collection(db, 'userSOPReadConfirmations'), where('userId', '==', authUid))),
    getDocs(query(collection(db, 'userEducationProgress'), where('userId', '==', authUid))),
  ]);

  const sops = new Set();
  sopSnap.docs.forEach((d) => {
    const data = d.data();
    // isRead alanı olmayan eski kayıtlar okunmuş kabul edilir
    if (data.isRead !== false) sops.add(data.sopId);
  });

  const educations = new Set();
  eduSnap.docs.forEach((d) => {
    const data = d.data();
    if ((data.watchedProgress || 0) >= EDUCATION_COMPLETION_PERCENT) {
      educations.add(data.educationId);
    }
  });

  return { sops, educations };
};

/**
 * Kullanıcının tamamlanmamış atamalarını son tarih bilgisiyle birlikte döner.
 * Son tarihi olmayan atamalar da listelenir, yalnızca uyarı üretmezler.
 */
export const fetchPendingAssignments = async (role, authUid, now = new Date()) => {
  const [assignments, completed] = await Promise.all([
    fetchAssignmentsForRole(role),
    fetchCompletedIds(authUid),
  ]);

  return assignments
    .filter((item) =>
      item.assignmentType === 'SOP'
        ? !completed.sops.has(item.id)
        : !completed.educations.has(item.id)
    )
    .map((item) => {
      const dueDate = item[DUE_DATE_FIELD];
      return {
        id: item.id,
        type: item.assignmentType,
        name: item.name || item.title || 'İsimsiz atama',
        dueDate: dueDate || null,
        daysLeft: daysUntil(dueDate, now),
        dueSoon: isDueSoon(dueDate, now),
        overdue: isOverdue(dueDate, now),
        deadlineLabel: describeDeadline(dueDate, now),
      };
    })
    .sort((a, b) => {
      if (a.daysLeft === null) return 1;
      if (b.daysLeft === null) return -1;
      return a.daysLeft - b.daysLeft;
    });
};

/**
 * Yönetici görünümü için: hangi çalışanın hangi atamayı tamamladığı.
 *
 * Tamamlanma kayıtları Firebase Auth UID ile tutulduğu için `Users`
 * koleksiyonu üzerinden çalışan kaydına eşlenir (SOPReadersModal ile aynı
 * yöntem: önce Users.employeeId, yoksa e-posta eşleşmesi).
 *
 * @returns {Promise<{employees: Array, completedBySop: Map, completedByEducation: Map}>}
 */
export const fetchCompletionMatrix = async () => {
  const [employeesSnap, usersSnap, sopReadsSnap, eduProgressSnap] = await Promise.all([
    getDocs(collection(db, 'Employees')),
    getDocs(collection(db, 'Users')),
    getDocs(collection(db, 'userSOPReadConfirmations')),
    getDocs(collection(db, 'userEducationProgress')),
  ]);

  const employees = employeesSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((e) => Number(e.Active) === 1);

  const employeeByEmail = new Map();
  employees.forEach((emp) => {
    if (emp.eMail) employeeByEmail.set(String(emp.eMail).toLowerCase(), emp.id);
  });

  // Auth UID -> employeeId
  const uidToEmployeeId = new Map();
  usersSnap.docs.forEach((userDoc) => {
    const data = userDoc.data();
    if (data.employeeId) {
      uidToEmployeeId.set(userDoc.id, data.employeeId);
      return;
    }
    const email = String(data.email || data.eMail || '').toLowerCase();
    if (email && employeeByEmail.has(email)) {
      uidToEmployeeId.set(userDoc.id, employeeByEmail.get(email));
    }
  });

  const addTo = (map, key, employeeId) => {
    if (!key || !employeeId) return;
    if (!map.has(key)) map.set(key, new Set());
    map.get(key).add(employeeId);
  };

  const completedBySop = new Map();
  sopReadsSnap.docs.forEach((d) => {
    const data = d.data();
    if (data.isRead === false) return;
    addTo(completedBySop, data.sopId, uidToEmployeeId.get(data.userId));
  });

  const completedByEducation = new Map();
  eduProgressSnap.docs.forEach((d) => {
    const data = d.data();
    if ((data.watchedProgress || 0) < EDUCATION_COMPLETION_PERCENT) return;
    addTo(completedByEducation, data.educationId, uidToEmployeeId.get(data.userId));
  });

  return { employees, completedBySop, completedByEducation };
};

/** Bir atamanın hedeflediği çalışanlar (group alanına göre). */
export const employeesForGroups = (employees, groups = []) => {
  const normalized = Array.isArray(groups) ? groups : [groups].filter(Boolean);
  if (normalized.includes('Hepsi')) return employees;
  return employees.filter((emp) => normalized.includes(emp.role));
};
